import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { ApiError } from "../api/client";
import { useAcceptInvite } from "../api/auth";
import { AuthShell } from "../components/AuthShell";

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const accept = useAcceptInvite();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  if (!token) {
    return (
      <AuthShell title="Invitation invalide" subtitle="Aucun jeton dans l'URL.">
        <Link to="/login" className="text-sm text-ink dark:text-ink-dark underline">
          Aller à la connexion
        </Link>
      </AuthShell>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    try {
      await accept.mutateAsync({ token, password });
      navigate("/");
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError("Invitation invalide ou expirée. Demandez à votre administrateur d'en générer une nouvelle.");
      } else {
        setError("Erreur. Réessayez.");
      }
    }
  };

  return (
    <AuthShell
      title="Bienvenue sur Bunapp"
      subtitle="Choisissez un mot de passe pour activer votre compte."
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="password">
            Mot de passe
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
          />
          <p
            className={`text-xs mt-1 ${
              password.length === 0
                ? "text-muted dark:text-muted-dark"
                : password.length < 8
                ? "text-chip-red"
                : "text-chip-green"
            }`}
          >
            {password.length === 0
              ? "8 caractères minimum"
              : password.length < 8
              ? `Encore ${8 - password.length} caractère${8 - password.length > 1 ? "s" : ""}`
              : "✓ Longueur suffisante"}
          </p>
        </div>
        <div>
          <label className="text-xs text-muted dark:text-muted-dark block mb-1" htmlFor="confirm">
            Confirmation
          </label>
          <input
            id="confirm"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
          />
        </div>
        {error && <div className="text-sm text-chip-red" role="alert">{error}</div>}
        <button
          type="submit"
          disabled={accept.isPending}
          className="w-full px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-ink/90 disabled:opacity-50"
        >
          {accept.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Activer mon compte
        </button>
      </form>
    </AuthShell>
  );
}
